import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { debounce } from "lodash";
import moment from "moment-timezone";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "-");

  const tableColumns = [
    { label: "actions.name", key: "actions", sortable: false },
    { label: "axis_body_cam.devices.serial_number", key: "serial_number", sortable: true },
    { label: "axis_body_cam.devices.name", key: "name", sortable: true },
    { label: "axis_body_cam.devices.model", key: "model" },
    { label: "axis_body_cam.devices.active", key: "active" },
    { label: "axis_body_cam.devices.assigned_user", key: "assigned_user" },
    { label: "axis_body_cam.devices.system_controller", key: "system_controller" },
    { label: "unit.title", key: "units" },
    { label: "axis_body_cam.devices.proxy_rtsp_server", key: "proxy_rtsp_server_name" },
    { label: "axis_body_cam.devices.device_tag", key: "device_tag_name" },
    { label: "axis_body_cam.devices.reseller", key: "omniview_reseller_name" },
    { label: "axis_body_cam.devices.recordings_count", key: "recordings_count" },
    { label: "axis_body_cam.devices.last_synced_at", key: "last_synced_at", formatter: formatDate },
  ];

  store.dispatch("tenant/getDeviceTags");
  const deviceTagOptions = computed(() =>
    (store.getters["tenant/getDeviceTags"] || []).map((tag) => ({
      value: tag.id,
      label: tag.name,
    }))
  );

  store.dispatch("omniview_reseller/getAll");
  const resellerOptions = computed(() => store.getters["omniview_reseller/getSelectedItems"]);

  const isSidebarActive = ref(false);
  const itemEdit = ref({});

  const openEdit = (item) => {
    itemEdit.value = item;
    isSidebarActive.value = true;
  };

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("axis_body_cam_devices/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      })
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  const update = (data) => {
    currentPage.value = data;
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  return {
    tableColumns,
    perPage,
    perPageOptions,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    update,
    deviceTagOptions,
    resellerOptions,
    isSidebarActive,
    itemEdit,
    openEdit,
  };
}
